import Papa from 'papaparse';
import { readSheet } from 'read-excel-file/browser';

// Sheet tab name in the price parity Excel export
const PARITY_REPORT_TAB = 'LPC Exec';

export interface VnsData {
  matchedName: string;
  offerId: string;
  totalMargin: number;
  mpv: number;
  pageViews: number;
  lpcRatio: number;
  lpcMargin: number;
}

export interface SalesforceData {
  cam: string;
  stage: string;
  description: string;
  opportunityName: string;
}

export interface ParityData {
  views: number;
  pctImpressionsOverpriced: number;
  avgMainPriceDiffOverpriced: number;
  margin: number;
  mpv: number;
}

function normalise(s: string): string {
  return s.toLowerCase().trim();
}

function parseNumberCell(value: string | undefined, fallback = 0): number {
  if (!value) return fallback;
  const cleaned = value.replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function parseCsv(content: string): Record<string, string>[] {
  return Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
  }).data;
}

export function parseVnsReport(fileContent: string, propertyName: string): VnsData {
  const rows = parseCsv(fileContent);
  const needle = normalise(propertyName);
  const exactMatches = rows.filter((r) => normalise(r['Property Name'] ?? '') === needle);
  const matches =
    exactMatches.length > 0 ? exactMatches : rows.filter((r) => normalise(r['Property Name'] ?? '').includes(needle));

  if (matches.length === 0) {
    throw new Error(`Property "${propertyName}" not found in VNS file.`);
  }
  if (matches.length > 1) {
    const names = matches.map((r) => r['Property Name']).join(', ');
    throw new Error(`Ambiguous match in VNS file. Found: ${names}. Please be more specific.`);
  }

  const row = matches[0];
  const offerId = row['offer_id']?.trim();
  if (!offerId) {
    throw new Error(`Property "${propertyName}" found in VNS but has no offer_id.`);
  }

  const totalMargin = parseNumberCell(row['Total Margin']);
  const lpcRatio = parseNumberCell(row['LPC to Total Item Ratio']);

  return {
    matchedName: row['Property Name'] ?? propertyName,
    offerId,
    totalMargin,
    mpv: parseNumberCell(row['MpV']),
    pageViews: Math.trunc(parseNumberCell(row['page_views'])),
    lpcRatio,
    lpcMargin: totalMargin * lpcRatio,
  };
}

const PARITY_REQUIRED_COLUMNS = [
  'Offer ID',
  'Views',
  '% Impressions Overpriced Main',
  'Avg Main Price Diff % (Overpriced)',
  'Margin',
  'MpV',
] as const;

export async function parseParityReport(file: File, offerId: string): Promise<ParityData | null> {
  let rows: unknown[][];
  try {
    rows = (await readSheet(file, PARITY_REPORT_TAB)) as unknown[][];
  } catch (err) {
    throw new Error(
      `Could not read the parity report. Make sure you uploaded the correct Price Parrot Excel file.${err instanceof Error ? ` (${err.message})` : ''}`,
      { cause: err },
    );
  }

  if (rows.length < 2) {
    throw new Error('Parity report appears to be empty. Check the uploaded file.');
  }

  const headers = rows[0].map((h) => String(h ?? ''));
  const idx = (col: string) => headers.indexOf(col);

  const missing = PARITY_REQUIRED_COLUMNS.filter((col) => idx(col) === -1);
  if (missing.length > 0) {
    throw new Error(`Parity report is missing required columns: ${missing.join(', ')}.`);
  }

  const offerIdIdx = idx('Offer ID');
  const match = rows.slice(1).find((row) => String(row[offerIdIdx] ?? '').trim() === offerId.trim());
  if (!match) return null;

  const num = (col: string): number => {
    const raw = match[idx(col)];
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    return parseNumberCell(String(raw ?? ''));
  };

  return {
    views: num('Views'),
    pctImpressionsOverpriced: num('% Impressions Overpriced Main'),
    avgMainPriceDiffOverpriced: num('Avg Main Price Diff % (Overpriced)'),
    margin: num('Margin'),
    mpv: num('MpV'),
  };
}

export function parseSalesforceReport(buffer: ArrayBuffer, propertyName: string): SalesforceData | null {
  const text = new TextDecoder('latin1').decode(buffer);
  const rows = parseCsv(text);
  const needle = normalise(propertyName);
  const matches = rows.filter((r) => normalise(r['Opportunity Name'] ?? '').includes(needle));
  if (matches.length === 0) return null;

  matches.sort((a, b) => {
    const dateA = a['Close Date'] ? new Date(a['Close Date']).getTime() : 0;
    const dateB = b['Close Date'] ? new Date(b['Close Date']).getTime() : 0;
    return dateB - dateA;
  });

  const row = matches[0];
  return {
    cam: row['Connectivity Account Manager'] || 'Not on record',
    stage: row['Stage'] ?? '',
    description: row['Description'] ?? '',
    opportunityName: row['Opportunity Name'] ?? '',
  };
}
